import { useQuery } from '@tanstack/react-query';
import { getRunStatus, IntakeApiError } from '../api/intake-client';
import type { IntakeRunStatus } from '../types';

/**
 * Retry rule for the intake queries: one retry for a network error or a 5xx, none for a 4xx (`intake_disabled`, a
 * deleted run, a 401), which the next read would only repeat.
 */
export const intakeQueryRetry = (failures: number, error: unknown) =>
  !(error instanceof IntakeApiError && error.status > 0 && error.status < 500) && failures < 1;

/** The screens show intake errors themselves: no global "Something went wrong" toast (src/app/providers/query-client.ts). */
export const INTAKE_QUERY_META = { skipGlobalError: true } as const;
export const INTAKE_MUTATION_META = { handledInline: true } as const;

/** Phase 1 has no push notifications: the contract asks for a poll every 3 s while a run is Queued or Running. */
export const INTAKE_POLL_MS = 3000;

export const isRunInFlight = (status: string | undefined) => status === 'Queued' || status === 'Running';

export const intakeRunStatusKey = (patientId: string, runId: string) => ['intake-run-status', patientId, runId] as const;

/**
 * The poll rule, on the query's state: every 3 s until the first reply and while the run is in flight. A failed FIRST
 * read stops it (a deleted run or `intake_disabled` would otherwise be asked for forever); a failed refresh with a
 * Queued or Running status on screen keeps polling, so one dropped request never freezes the status.
 */
export function runStatusRefetchInterval(state: { status: string; data: IntakeRunStatus | undefined }): number | false {
  if (state.status === 'error' && state.data === undefined) return false;
  return !state.data || isRunInFlight(state.data.status) ? INTAKE_POLL_MS : false;
}

/** One run's status, polled while it is in flight and left alone once it settles. */
export function useIntakeRunStatus(patientId: string, runId: string | null | undefined) {
  return useQuery<IntakeRunStatus>({
    queryKey: intakeRunStatusKey(patientId, runId ?? ''),
    queryFn: ({ signal }) => getRunStatus(patientId, runId as string, signal),
    enabled: !!patientId && !!runId,
    refetchInterval: (query) => runStatusRefetchInterval(query.state),
    retry: intakeQueryRetry,
    meta: INTAKE_QUERY_META,
    refetchIntervalInBackground: false,
  });
}
